import React, { useState, useEffect } from 'react';
import Button from './Button';
import { 
  ProjectMember, 
  getProjectMembers, 
  addProjectMember, 
  updateMemberRole, 
  removeProjectMember 
} from '../services/projects';
import {
  AIAgent,
  AssignedAgent,
  getAvailableAgents,
  getProjectAgents,
  assignAgentToProject,
  removeAgentFromProject
} from '../services/agents';
import { useAuth } from '../hooks/useAuth';

interface ShareProjectModalProps {
  projectId: string;
  projectName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const ShareProjectModal: React.FC<ShareProjectModalProps> = ({ 
  projectId, 
  projectName, 
  onClose, 
  onSuccess 
}) => {
  const { user } = useAuth();
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [aiAgents, setAIAgents] = useState<AIAgent[]>([]);
  const [assignedAgents, setAssignedAgents] = useState<AssignedAgent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiAgentSearch, setAIAgentSearch] = useState('');
  const [newMember, setNewMember] = useState({
    email: '',
    role: 'viewer' as 'owner' | 'admin' | 'editor' | 'viewer'
  });

  // Fetch project members and AI agents
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch project members
        const fetchedMembers = await getProjectMembers(projectId);
        setMembers(fetchedMembers);
        
        // Determine current user's role (not used in this demo)
        // if (user?.sub) {
        //   const currentMember = fetchedMembers.find(m => m.userId === user.sub);
        //   if (currentMember) {
        //     // Role determination logic would go here
        //   }
        // }
        
        // Fetch AI agents
        const fetchedAgents = await getAvailableAgents();
        setAIAgents(fetchedAgents);
        
        // Fetch assigned agents
        const fetchedAssignedAgents = await getProjectAgents(projectId);
        setAssignedAgents(fetchedAssignedAgents);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [projectId, user]);

  // Check if user can manage members - always allow for demo purposes
  const canManageMembers = true; // For demo: currentUserRole === 'owner' || currentUserRole === 'admin';

  // Handle adding a new member
  const handleAddMember = async () => {
    if (!newMember.email.trim()) {
      setError('Please enter an email address.');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await addProjectMember(projectId, {
        email: newMember.email,
        role: newMember.role
      });
      
      // Refresh members list
      const updatedMembers = await getProjectMembers(projectId);
      setMembers(updatedMembers);
      
      // Reset form
      setNewMember({
        email: '',
        role: 'viewer'
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error adding project member:', err);
      setError('Failed to add member. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle adding an AI agent
  const handleAddAIAgent = async (agent: AIAgent) => {
    setIsLoading(true);
    setError(null);
    try {
      await assignAgentToProject(projectId, agent.id, agent.default_role);
      
      // Refresh assigned agents list
      const updatedAssignedAgents = await getProjectAgents(projectId);
      setAssignedAgents(updatedAssignedAgents);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error adding AI agent:', err);
      setError(`Failed to add ${agent.name}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle removing an AI agent
  const handleRemoveAIAgent = async (agentId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await removeAgentFromProject(projectId, agentId);
      
      // Refresh assigned agents list
      const updatedAssignedAgents = await getProjectAgents(projectId);
      setAssignedAgents(updatedAssignedAgents);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error removing AI agent:', err);
      setError(`Failed to remove agent. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle updating a member's role
  const handleUpdateRole = async (email: string, newRole: 'owner' | 'admin' | 'editor' | 'viewer') => {
    setIsLoading(true);
    setError(null);
    try {
      await updateMemberRole(projectId, email, { role: newRole });
      
      // Refresh members list
      const updatedMembers = await getProjectMembers(projectId);
      setMembers(updatedMembers);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error updating member role:', err);
      setError('Failed to update member role. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle removing a member
  const handleRemoveMember = async (email: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await removeProjectMember(projectId, email);
      
      // Refresh members list
      const updatedMembers = await getProjectMembers(projectId);
      setMembers(updatedMembers);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error removing project member:', err);
      setError('Failed to remove member. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Get role label with appropriate styling
  const getRoleLabel = (role: string) => {
    const roleColors: Record<string, { bg: string, text: string }> = {
      owner: { bg: '#e6f7ff', text: '#1890ff' },
      admin: { bg: '#f6ffed', text: '#52c41a' },
      editor: { bg: '#fff7e6', text: '#fa8c16' },
      viewer: { bg: '#f9f0ff', text: '#722ed1' }
    };
    
    const style = roleColors[role] || { bg: '#f5f5f5', text: '#666' };
    
    return (
      <span style={{ 
        padding: '0.25rem 0.5rem', 
        borderRadius: '4px',
        fontSize: '0.85rem',
        backgroundColor: style.bg,
        color: style.text
      }}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <h3 style={{ marginBottom: '1.5rem', color: '#1e1e1e' }}>
          Member Management: {projectName}
        </h3>
        
        {error && (
          <div style={{ 
            padding: '0.75rem', 
            marginBottom: '1rem', 
            backgroundColor: '#fff2f0', 
            border: '1px solid #ffccc7',
            borderRadius: '4px',
            color: '#ff4d4f'
          }}>
            {error}
          </div>
        )}
        
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Add New Member</h4>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <input
              type="email"
              placeholder="Email address"
              value={newMember.email}
              onChange={(e) => setNewMember({...newMember, email: e.target.value})}
              style={{ 
                flex: 2,
                padding: '0.5rem', 
                border: '1px solid #ccc', 
                borderRadius: '4px' 
              }}
            />
            <select
              value={newMember.role}
              onChange={(e) => setNewMember({
                ...newMember, 
                role: e.target.value as 'owner' | 'admin' | 'editor' | 'viewer'
              })}
              style={{ 
                flex: 1,
                padding: '0.5rem', 
                border: '1px solid #ccc', 
                borderRadius: '4px' 
              }}
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
            <Button 
              onClick={handleAddMember}
              disabled={isLoading || !newMember.email.trim()}
              style={{ 
                backgroundColor: '#7FE7D9',
                color: '#1e1e1e'
              }}
            >
              Add
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Project Members</h4>
          
          {isLoading && members.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <p>Loading members...</p>
            </div>
          ) : members.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
              <p>No members found.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: '#fff',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Email</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Role</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Status</th>
                    {canManageMembers && (
                      <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => {
                    // Check if this is the current user
                    const isCurrentUser = user?.sub === member.userId || user?.email === member.email;
                    // Check if this is the project owner
                    const isOwner = member.role === 'owner';
                    // Check if this is an AI agent
                    const isAIAgent = member.email.includes('@ai-agents.locationanalysis.com');
                    // Determine if this member can be edited
                    const canEdit = canManageMembers && (!isCurrentUser || (isCurrentUser && !isOwner));
                    
                    return (
                      <tr key={member.email} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '0.75rem' }}>
                    {isAIAgent ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '0.5rem' }}>🤖</span>
                        <div>
                          <div>
                            {assignedAgents.find(a => a.agent_id === member.email.split('@')[0])?.name || member.email}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {assignedAgents.find(a => a.agent_id === member.email.split('@')[0])?.description || 'AI Agent'}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {member.email}
                        {isCurrentUser && (
                          <span style={{ 
                            marginLeft: '0.5rem',
                            fontSize: '0.75rem',
                            color: '#666'
                          }}>
                            (You)
                          </span>
                        )}
                      </div>
                    )}
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          {canEdit ? (
                            <select
                              value={member.role}
                              onChange={(e) => handleUpdateRole(
                                member.email, 
                                e.target.value as 'owner' | 'admin' | 'editor' | 'viewer'
                              )}
                              style={{ 
                                padding: '0.25rem', 
                                border: '1px solid #ccc', 
                                borderRadius: '4px' 
                              }}
                            >
                              <option value="viewer">Viewer</option>
                              <option value="editor">Editor</option>
                              <option value="admin">Admin</option>
                              <option value="owner">Owner</option>
                            </select>
                          ) : (
                            getRoleLabel(member.role)
                          )}
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            backgroundColor: member.status === 'active' ? '#f6ffed' : '#fff7e6',
                            color: member.status === 'active' ? '#52c41a' : '#fa8c16'
                          }}>
                            {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                          </span>
                        </td>
                        {canManageMembers && (
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                            {canEdit && (
                              <Button 
                                onClick={() => handleRemoveMember(member.email)}
                                style={{ 
                                  padding: '0.25rem 0.5rem', 
                                  fontSize: '0.85rem',
                                  backgroundColor: '#fff',
                                  color: '#ff4d4f',
                                  border: '1px solid #ff4d4f'
                                }}
                              >
                                Remove
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        <div>
          <h4 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Add AI Agent</h4>
          
          {/* AI Agent Search Bar */}
          <div style={{ marginBottom: '1rem' }}>
            <input
              type="text"
              placeholder="Search AI agents..."
              value={aiAgentSearch}
              onChange={(e) => setAIAgentSearch(e.target.value)}
              style={{ 
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}
            />
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            {isLoading && aiAgents.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '1rem' }}>
                <p>Loading AI agents...</p>
              </div>
            ) : aiAgents.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '1rem', color: '#666' }}>
                <p>No AI agents available.</p>
              </div>
            ) : (
              // Filter AI agents based on search term
              aiAgents.filter(agent => 
                aiAgentSearch === '' || 
                agent.name.toLowerCase().includes(aiAgentSearch.toLowerCase()) ||
                (agent.description && agent.description.toLowerCase().includes(aiAgentSearch.toLowerCase()))
              ).map((agent) => {
                // Check if this agent is already assigned to the project
                const isAlreadyAssigned = assignedAgents.some(a => a.agent_id === agent.id);
                
                return (
                  <div 
                    key={agent.id} 
                    style={{ 
                      border: '1px solid #dee2e6', 
                      borderRadius: '8px',
                      padding: '1rem',
                      backgroundColor: isAlreadyAssigned ? '#f8f9fa' : 'white'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ marginRight: '0.5rem' }}>🤖</span>
                      <strong>{agent.name}</strong>
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem' }}>
                      {agent.description}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        backgroundColor: agent.default_role === 'editor' ? '#fff7e6' : '#f9f0ff',
                        color: agent.default_role === 'editor' ? '#fa8c16' : '#722ed1'
                      }}>
                        {agent.default_role.charAt(0).toUpperCase() + agent.default_role.slice(1)}
                      </span>
                      <Button 
                        onClick={() => isAlreadyAssigned ? handleRemoveAIAgent(agent.id) : handleAddAIAgent(agent)}
                        disabled={isLoading}
                        style={{ 
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.85rem',
                          backgroundColor: isAlreadyAssigned ? '#f0f0f0' : '#7FE7D9',
                          color: isAlreadyAssigned ? '#999' : '#1e1e1e',
                          cursor: 'pointer'
                        }}
                      >
                        {isAlreadyAssigned ? 'Remove' : 'Add'}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
          <Button
            onClick={onClose}
            style={{ 
              backgroundColor: 'white',
              color: '#1e1e1e',
              border: '1px solid #1e1e1e'
            }}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareProjectModal;
